import { useEffect, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Card, Chip, Icon } from '@/components/kit';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { SettingsRail } from './SettingsRail';
import { TextEditor } from './editors';
import { SettingsUnreadable } from './Unreadable';
import { useRecordingLimits, useSaveRecordingLimits, type RecordingLimits } from './useSettings';

/**
 * Clinic manager · Settings ▸ Recording — `g_set_rec.dc.html`.
 *
 * TWO NUMBERS, AND WHAT THEY ARE BOUNDED BY. The two editable cards are the
 * policy; the two ghost cards beside them are not settings at all — they are
 * the server's own clamps, printed so nobody types 900 and wonders why 600
 * came back. `SystemSettingsService` clamps max to [5, 600] and silence to
 * [2, 120] with 0 meaning "never", on read AND on write, so the numbers in
 * the ghost cards are the real bounds and not a caption about them.
 *
 * The limits drive the recording dock every doctor in the clinic opens
 * (`GET /system-settings/frontend/recording-limits`), which is why this is one
 * of the few Settings rooms whose effect is felt the same day.
 */
export function ManagerRecordingPage() {
  const narrow = useNarrowViewport();
  const go = useNavigate();
  const { value, isLoading, error, refetch } = useRecordingLimits();
  const save = useSaveRecordingLimits();
  const [draft, setDraft] = useState<RecordingLimits | null>(null);
  const [editing, setEditing] = useState<'max' | 'silence' | null>(null);
  const [query, setQuery] = useState('');
  const [bad, setBad] = useState<string | null>(null);

  usePageMeta({ title: 'Recording · System Settings' });

  useEffect(() => { if (value && !draft) setDraft(value); }, [value, draft]);

  const shown = draft ?? value;
  const dirty = !!draft && !!value && JSON.stringify(draft) !== JSON.stringify(value);
  const reading = isLoading && !shown;
  const unreadable = !reading && !shown;

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);
  const anyShown = ['max session 60 min', 'silence auto-stop', 'allowed range', '0 means']
    .some((t) => matches(t));

  const crumb = usePhoneCrumb('Settings', 'Recording');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'Recording',
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> Recording
        </>
      ),
      search: unreadable ? null : 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="recording" />,
      extra: dirty ? <Chip kind="warn" small className="w6">⚠ unsaved</Chip> : undefined,
      primary: draft ? {
        label: save.isPending ? 'Saving…' : 'Save',
        onClick: () => save.mutate(draft, { onSuccess: (next) => setDraft(next) }),
      } : undefined,
    },
    [query, dirty, save.isPending, !!draft, unreadable],
  );

  /* THE EDITOR TAKES MINUTES AND THE SERVER TAKES MINUTES, so the only thing
     between them is the clamp — and a number outside it is refused HERE, with
     the bound named, instead of being silently rewritten on the way back. */
  const applyNumber = (kind: 'max' | 'silence', text: string) => {
    const n = Math.round(Number(text));
    if (!Number.isFinite(n)) { setBad('That is not a number of minutes.'); return; }
    if (kind === 'max' && (n < 5 || n > 600)) {
      setBad('A session limit is between 5 and 600 minutes.'); return;
    }
    if (kind === 'silence' && n !== 0 && (n < 2 || n > 120)) {
      setBad('A silence auto-stop is 0, or between 2 and 120 minutes.'); return;
    }
    setBad(null);
    setDraft((d) => (d ? (kind === 'max'
      ? { ...d, maxRecordingMinutes: n }
      : { ...d, silenceTimeoutMinutes: n }) : d));
  };

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {reading ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading this clinic’s recording limits…</p>
        ) : null}

        {unreadable ? (
          <SettingsUnreadable what="recording policy" error={error} onRetry={() => { void refetch(); }} />
        ) : null}

        {shown ? (
          <>
            {anyShown ? (
              <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>
                Recording limits — every recorded session; raise them for therapy-length visits
              </p>
            ) : null}

            {matches('max session') || matches('silence auto-stop') ? (
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {matches('max session') ? (
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('max')}>
                    <p className="t-ovl mut">Max session</p>
                    <p className="t-strong mt4">
                      {shown.maxRecordingMinutes} min <Icon name="pencil" size="sm" />
                    </p>
                    {/* `g_t12` — on a phone the range sits under the value; the desk keeps it as its own card. */}
                    {narrow ? <p className="t-cap mut">5–600</p> : null}
                  </Card>
                ) : null}
                {matches('silence auto-stop') ? (
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('silence')}>
                    <p className="t-ovl mut">Silence auto-stop</p>
                    <p className="t-strong mt4">
                      {shown.silenceTimeoutMinutes === 0
                        ? 'never'
                        : `${shown.silenceTimeoutMinutes} min`} <Icon name="pencil" size="sm" />
                    </p>
                    {narrow ? <p className="t-cap mut">0 = never</p> : null}
                  </Card>
                ) : null}
              </div>
            ) : null}

            {!narrow && (matches('allowed range') || matches('0 means')) ? (
              <div className="cm-fields" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {matches('allowed range') ? (
                  <Card kind="ghost" style={{ flex: 1, margin: 0 }}>
                    <p className="t-ovl mut">Allowed range</p>
                    <p className="t-strong mt4">5 – 600 minutes</p>
                  </Card>
                ) : null}
                {matches('0 means') ? (
                  <Card kind="ghost" style={{ flex: 1, margin: 0 }}>
                    <p className="t-ovl mut">0 means</p>
                    <p className="t-strong mt4">never stop on silence</p>
                  </Card>
                ) : null}
              </div>
            ) : null}

            {!anyShown ? (
              <p className="t-sec mut" style={{ marginTop: 18 }}>
                No setting on this screen matches that. Clear the search to see the limits again.
              </p>
            ) : null}

            <Card kind="accent">
              <p className="t-sec brand">
                Consent is implicit in the act: starting ambient stamps “verbal consent taken by
                Dr. X” into the note. The patient disclosure text lives under Privacy.{' '}
                <button type="button" className="w6 brand"
                  style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                  onClick={() => go('/clinic-manager/settings/privacy')}>
                  Open Privacy ›
                </button>
              </p>
            </Card>

            {bad ? <p className="t-sec red" style={{ marginTop: 10 }}>{bad}</p> : null}
            {save.isError ? (
              <p className="t-sec red" style={{ marginTop: 10 }}>
                That did not save. Nothing was changed on the server — try again, or reload to see
                what is stored.
              </p>
            ) : null}
            {save.isSuccess && !dirty ? <p className="t-sec grn" style={{ marginTop: 10 }}>Saved.</p> : null}
          </>
        ) : null}
      </div>

      <TextEditor
        open={editing !== null}
        title={editing === 'silence' ? 'Silence auto-stop' : 'Max session'}
        sub={editing === 'silence'
          ? 'minutes of sustained silence before a recording finalises itself — 0 never stops'
          : 'the hard cap on one recorded session, in minutes'}
        value={String(editing === 'silence'
          ? (shown?.silenceTimeoutMinutes ?? 0)
          : (shown?.maxRecordingMinutes ?? 0))}
        onClose={() => setEditing(null)}
        onDone={(next) => applyNumber(editing ?? 'max', next)}
      />
    </div>
  );
}
